/* eslint-disable no-console */
import {formatIndian} from '../../../../../utils/formatIndian';

export const TCO2E_TO_KT_THRESHOLD = 999.99;

export enum ScenarioEnum {
  ABSOLUTE_1_5C = 'Absolute Scenario- 1.5°C',
  ABSOLUTE_WELL_BELOW_2C = 'Absolute Scenario- Well below 2°C'
}

const formatTco2eOrKtco2e = (value: number): string => {
  const abs = Math.abs(value);
  if (abs > TCO2E_TO_KT_THRESHOLD) {
    const kValue = value / 1000;
    const formatted = kValue.toFixed(2);

    return `${formatted} KtCO2e`;
  }

  return `${formatIndian(Number(value.toFixed(2)))} tCO2e`;
};

export const formatBenchmarkValue = (value: number, unit: string): string => {
  if (value === 0) return `0 ${unit}`;
  const abs = Math.abs(value);
  if (abs < 0.0001 || abs >= 1e12) {
    return `${value.toExponential(2)} ${unit}`;
  }
  if (unit === 'tCO2e' && abs > TCO2E_TO_KT_THRESHOLD) {
    return formatTco2eOrKtco2e(value);
  }
  const formatted = Number(value.toFixed(4));

  return `${formatIndian(formatted)} ${unit}`;
};
const tooltipIndustryBenchmark = {
  'Scope 1 GHG emissions':
    'Scope 1 includes direct emissions from owned or controlled sources like facilities and vehicles.',
  'Scope 2 GHG emissions':
    'Scope 2 covers indirect emissions from purchased electricity and steam.',
  'Scope 3 GHG emissions':
    'Scope 3 accounts for all other indirect emissions occurring in the value chain.',
  'Scope 1 GHG intensity':
    'Scope 1 Intensity reflects direct emissions per unit of organizational output.',
  'Scope 2 GHG intensity':
    'Scope 2 Intensity measures indirect emissions from energy use per organizational unit.',
  'Scope 3 GHG intensity':
    'Scope 3 Intensity aggregates all indirect value chain emissions per unit output.',
  'Total Water Consumption':
    'Shows your company’s total water use compared against the industry benchmark.',
  'Total quantity of waste generated':
    'Displays your company’s total waste generation in comparison with industry standards.',
  'Energy consumption inside organization':
    'Compares your company’s total energy usage with the industry benchmark.'
};

export const scopeToCardItems = (scopeData: any[]): any[] => {
  return scopeData?.map((item) => {
    const value = item.value ?? 0;
    const benchmark = item.benchmark ?? 0;
    const isAboveBenchmark =
      isNaN(benchmark) || isNaN(value) ? false : value > benchmark;
    const label = item.label;
    const tooltip =
      tooltipIndustryBenchmark?.[
        item?.dataId as keyof typeof tooltipIndustryBenchmark
      ] ?? '';

    return {
      id: item.dataId,
      label,
      value,
      benchmark,
      unit: item.unit ?? 'tCO2e',
      isAboveBenchmark,
      category: 'scope',
      tooltip
    };
  });
};

export const intensityToCardItems = (intensityData: any[]): any[] => {
  return intensityData?.map((item) => {
    const value = item.value ?? 0;
    const benchmark = item.benchmark ?? 0;
    const isAboveBenchmark =
      isNaN(benchmark) || isNaN(value) ? false : value > benchmark;
    const label = item.label;
    const tooltip =
      tooltipIndustryBenchmark?.[
        item?.dataId as keyof typeof tooltipIndustryBenchmark
      ] ?? '';

    return {
      id: item.dataId,
      label,
      value,
      benchmark,
      unit: item.unit ?? 'tCO2e/INR',
      isAboveBenchmark,
      category: 'intensity',
      tooltip
    };
  });
};

export const consumptionToCardItems = (consumptionData: any[]): any[] => {
  return consumptionData?.map((item) => {
    const value = item.value ?? 0;
    const benchmark = item.benchmark ?? 0;
    const isAboveBenchmark =
      isNaN(benchmark) || isNaN(value) ? false : value > benchmark;
    const label = item.label;
    const tooltip =
      tooltipIndustryBenchmark?.[
        item?.dataId as keyof typeof tooltipIndustryBenchmark
      ] ?? '';

    return {
      id: item.dataId,
      label,
      value,
      benchmark,
      unit: item.unit ?? '',
      isAboveBenchmark,
      category: 'consumption',
      tooltip
    };
  });
};

export const buildBenchmarkCards = (
  scopeData: any[],
  intensityData: any[],
  consumptionData: any[]
): any[] => {
  return [
    ...(scopeToCardItems(scopeData) ?? []),
    ...(intensityToCardItems(intensityData) ?? []),
    ...(consumptionToCardItems(consumptionData) ?? [])
  ];
};

export const aboveBenchmarkTags = (cards: any[]): any[] => {
  return cards?.filter((c) => c.isAboveBenchmark) ?? [];
};

export const totalEmissionsFromScope = (scopeData: any[]): number => {
  return scopeData?.reduce((sum, item) => sum + item.value, 0) ?? 0;
};
